/**
 * MVD Validator
 * 
 * Validates model against Model View Definitions for IFC compliance.
 */

// ==================== Types ====================

export type ValidationSeverity = 'Error' | 'Warning' | 'Info';
export type MVDType = 'ReferenceView' | 'DesignTransferView' | 'CoordinationView' | 'Custom';

export interface ValidationRule {
    id: string;
    name: string;
    description: string;
    severity: ValidationSeverity;
    applicableCategories: string[];
    requiredProperties?: string[];
    requiredRelationships?: string[];
    geometryRequirements?: {
        requiresGeometry: boolean;
        allowedRepresentations: string[];
    };
    validate: (element: any) => ValidationResult;
}

export interface ValidationResult {
    passed: boolean;
    ruleId: string;
    ruleName: string;
    severity: ValidationSeverity;
    elementId?: string;
    message: string;
}

export interface MVDReport {
    mvdType: MVDType;
    timestamp: number;
    totalElements: number;
    passedElements: number;
    failedElements: number;
    results: ValidationResult[];
    summary: {
        errors: number;
        warnings: number;
        info: number;
    };
}

// ==================== Standard Rules ====================

const REFERENCE_VIEW_RULES: ValidationRule[] = [
    {
        id: 'rv-geometry',
        name: 'Geometry Required',
        description: 'All elements must have geometry representation',
        severity: 'Error',
        applicableCategories: ['Walls', 'Floors', 'Roofs', 'Doors', 'Windows', 'Structural Columns', 'Structural Framing'],
        geometryRequirements: {
            requiresGeometry: true,
            allowedRepresentations: ['SweptSolid', 'Brep', 'Tessellation']
        },
        validate: (element) => {
            const hasGeometry = element.geometryData &&
                (element.geometryData.start || element.geometryData.boundary || element.geometryData.position);
            return {
                passed: hasGeometry,
                ruleId: 'rv-geometry',
                ruleName: 'Geometry Required',
                severity: 'Error',
                elementId: element.id,
                message: hasGeometry ? 'Element has valid geometry' : 'Element is missing geometry'
            };
        }
    },
    {
        id: 'rv-type',
        name: 'Type Required',
        description: 'All elements must have a type name',
        severity: 'Warning',
        applicableCategories: ['*'],
        validate: (element) => {
            const hasType = element.type && element.type.trim() !== '';
            return {
                passed: hasType,
                ruleId: 'rv-type',
                ruleName: 'Type Required',
                severity: 'Warning',
                elementId: element.id,
                message: hasType ? 'Element has type defined' : 'Element is missing type name'
            };
        }
    },
    {
        id: 'rv-level',
        name: 'Level Association',
        description: 'Elements should be associated with a building storey',
        severity: 'Warning',
        applicableCategories: ['Walls', 'Floors', 'Doors', 'Windows'],
        validate: (element) => {
            const hasLevel = element.parameters?.['Level'] || element.level;
            return {
                passed: !!hasLevel,
                ruleId: 'rv-level',
                ruleName: 'Level Association',
                severity: 'Warning',
                elementId: element.id,
                message: hasLevel ? 'Element has level association' : 'Element is not associated with a level'
            };
        }
    }
];

const DESIGN_TRANSFER_RULES: ValidationRule[] = [
    ...REFERENCE_VIEW_RULES,
    {
        id: 'dt-pset',
        name: 'Property Sets Required',
        description: 'Elements must have standard property sets',
        severity: 'Error',
        applicableCategories: ['Walls', 'Floors', 'Roofs'],
        requiredProperties: ['Reference', 'LoadBearing', 'IsExternal'],
        validate: (element) => {
            const params = element.parameters || {};
            const hasRequired = ['Structural', 'Function'].some(p => params[p] !== undefined);
            return {
                passed: hasRequired,
                ruleId: 'dt-pset',
                ruleName: 'Property Sets Required',
                severity: 'Error',
                elementId: element.id,
                message: hasRequired ? 'Required properties present' : 'Missing required properties'
            };
        }
    }
];

// ==================== MVD Validator ====================

export class MVDValidator {
    private rules: Map<MVDType, ValidationRule[]> = new Map();
    private customRules: ValidationRule[] = [];

    constructor() {
        this.rules.set('ReferenceView', REFERENCE_VIEW_RULES);
        this.rules.set('DesignTransferView', DESIGN_TRANSFER_RULES);
        this.rules.set('CoordinationView', REFERENCE_VIEW_RULES);  // Simplified
    }

    // ==================== Validation ====================

    public validateModel(elements: any[], mvdType: MVDType): MVDReport {
        const rules = this.getRulesForMVD(mvdType);
        const results: ValidationResult[] = [];
        let passedElements = 0;
        let failedElements = 0;

        for (const element of elements) {
            let elementPassed = true;
            const applicableRules = rules.filter(r =>
                r.applicableCategories.includes('*') ||
                r.applicableCategories.includes(element.category)
            );

            for (const rule of applicableRules) {
                const result = rule.validate(element);
                results.push(result);

                if (!result.passed) {
                    elementPassed = false;
                }
            }

            if (elementPassed) {
                passedElements++;
            } else {
                failedElements++;
            }
        }

        // Calculate summary
        const summary = {
            errors: results.filter(r => !r.passed && r.severity === 'Error').length,
            warnings: results.filter(r => !r.passed && r.severity === 'Warning').length,
            info: results.filter(r => !r.passed && r.severity === 'Info').length
        };

        return {
            mvdType,
            timestamp: Date.now(),
            totalElements: elements.length,
            passedElements,
            failedElements,
            results,
            summary
        };
    }

    private getRulesForMVD(mvdType: MVDType): ValidationRule[] {
        const standardRules = this.rules.get(mvdType) || REFERENCE_VIEW_RULES;
        return [...standardRules, ...this.customRules];
    }

    // ==================== Custom Rules ====================

    public addCustomRule(rule: ValidationRule): void {
        this.customRules.push(rule);
    }

    public removeCustomRule(ruleId: string): void {
        this.customRules = this.customRules.filter(r => r.id !== ruleId);
    }

    public getCustomRules(): ValidationRule[] {
        return [...this.customRules];
    }

    // ==================== Reports ====================

    public generateReport(report: MVDReport): string {
        const lines: string[] = [
            '═══════════════════════════════════════════════',
            `  MVD VALIDATION REPORT - ${report.mvdType}`,
            '═══════════════════════════════════════════════',
            '',
            `Date: ${new Date(report.timestamp).toLocaleString()}`,
            '',
            '─── SUMMARY ─────────────────────────────────────',
            `Total Elements:  ${report.totalElements}`,
            `Passed:          ${report.passedElements} (${(report.passedElements / report.totalElements * 100).toFixed(1)}%)`,
            `Failed:          ${report.failedElements} (${(report.failedElements / report.totalElements * 100).toFixed(1)}%)`,
            '',
            `Errors:    ${report.summary.errors}`,
            `Warnings:  ${report.summary.warnings}`,
            `Info:      ${report.summary.info}`,
            '',
        ];

        if (report.summary.errors > 0) {
            lines.push('─── ERRORS ────────────────────────────────────');
            for (const r of report.results.filter(r => !r.passed && r.severity === 'Error')) {
                lines.push(`  ✗ [${r.elementId?.slice(0, 8)}] ${r.ruleName}: ${r.message}`);
            }
            lines.push('');
        }

        if (report.summary.warnings > 0) {
            lines.push('─── WARNINGS ──────────────────────────────────');
            for (const r of report.results.filter(r => !r.passed && r.severity === 'Warning')) {
                lines.push(`  ⚠ [${r.elementId?.slice(0, 8)}] ${r.ruleName}: ${r.message}`);
            }
            lines.push('');
        }

        lines.push('═══════════════════════════════════════════════');
        lines.push(`  ${report.summary.errors === 0 ? '✓ MODEL PASSES MVD REQUIREMENTS' : '✗ MODEL DOES NOT PASS MVD REQUIREMENTS'}`);
        lines.push('═══════════════════════════════════════════════');

        return lines.join('\n');
    }

    // ==================== Quick Checks ====================

    public quickCheck(element: any, mvdType: MVDType = 'ReferenceView'): boolean {
        const rules = this.getRulesForMVD(mvdType);
        const applicableRules = rules.filter(r =>
            r.applicableCategories.includes('*') ||
            r.applicableCategories.includes(element.category)
        );

        for (const rule of applicableRules) {
            if (rule.severity === 'Error') {
                const result = rule.validate(element);
                if (!result.passed) return false;
            }
        }
        return true;
    }

    public getFailedRulesForElement(element: any, mvdType: MVDType = 'ReferenceView'): ValidationResult[] {
        const rules = this.getRulesForMVD(mvdType);
        const results: ValidationResult[] = [];

        for (const rule of rules) {
            if (rule.applicableCategories.includes('*') || rule.applicableCategories.includes(element.category)) {
                const result = rule.validate(element);
                if (!result.passed) {
                    results.push(result);
                }
            }
        }

        return results;
    }
}
